import { mask, unMask } from '@/lib/form-state/util'

// Helpers de moeda/recálculo de preço compartilhados entre o formulário do
// catálogo (`catalog-form-page.tsx`) e a aba "Catálogo do
// Parceiro/Profissional/Hospital" (`EntityCatalogTab`). Valores monetários são
// SEMPRE centavos inteiros; a exibição usa a máscara de moeda BR.

export const currencyFormat = (
    value: string | number | null | undefined,
    allowEmpty = false,
): string => {
    if (value === null || value === undefined) value = '0'
    const digits = `${value}`.replace(/[^0-9]+/g, '')
    if (
        allowEmpty &&
        (!digits || digits === '0' || digits === '00' || digits === '000')
    )
        return ''
    return mask(digits, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

export const parseCurrencyToCents = (value: string): number => {
    if (!value.trim()) return 0
    const unmaskedValue = unMask(value, 'currency')
    return parseInt(unmaskedValue) || 0
}

export const formatCentsToDisplay = (
    cents: number | null | undefined,
): string => {
    if (cents === null || cents === undefined || cents === 0) return ''
    return currencyFormat(cents.toString())
}

export const recalcSellingPriceFromMarkup = (
    priceCents: number,
    markup: number,
): string => {
    if (priceCents <= 0 || isNaN(markup)) return ''
    const sellingCents = Math.round(priceCents + (priceCents * markup) / 100)
    return formatCentsToDisplay(sellingCents)
}

export const recalcMarkupFromPrices = (
    priceCents: number,
    sellingCents: number,
): string => {
    if (priceCents <= 0) return ''
    const markup = ((sellingCents - priceCents) / priceCents) * 100
    return markup.toFixed(2)
}

/** Rascunho de preço editável (campos como string mascarada). */
export interface PriceDraft {
    price: string
    sellingPrice: string
    markupPercentage: string
}

/**
 * Aplica a mudança de um campo de preço mantendo o recálculo bidirecional
 * custo↔venda↔margem — idêntico ao `handleSubPriceChange` do formulário do
 * catálogo, porém operando sobre um único rascunho (imutável).
 */
export const applyPriceFieldChange = <T extends PriceDraft>(
    draft: T,
    field: 'price' | 'sellingPrice' | 'markupPercentage',
    value: string,
): T => {
    const item = { ...draft }

    if (field === 'price') {
        item.price = currencyFormat(value, true)
        const priceCents = parseCurrencyToCents(item.price)
        if (item.markupPercentage) {
            const markup = parseFloat(item.markupPercentage)
            if (!isNaN(markup)) {
                item.sellingPrice = recalcSellingPriceFromMarkup(priceCents, markup)
            }
        } else if (item.sellingPrice) {
            const sellingCents = parseCurrencyToCents(item.sellingPrice)
            if (sellingCents > 0) {
                item.markupPercentage = recalcMarkupFromPrices(priceCents, sellingCents)
            }
        }
    } else if (field === 'sellingPrice') {
        item.sellingPrice = currencyFormat(value, true)
        const sellingCents = parseCurrencyToCents(item.sellingPrice)
        const priceCents = parseCurrencyToCents(item.price)
        item.markupPercentage = recalcMarkupFromPrices(priceCents, sellingCents)
    } else if (field === 'markupPercentage') {
        item.markupPercentage = value
        const markup = parseFloat(value)
        const priceCents = parseCurrencyToCents(item.price)
        item.sellingPrice = !isNaN(markup)
            ? recalcSellingPriceFromMarkup(priceCents, markup)
            : ''
    }

    return item
}
